/**
 * DeleteReportDialog — confirmation modal for the report-delete cascade
 * (Workflow Prompt 7). Surfaces what will be removed before the user
 * commits, requires typed confirmation matching the report title for the
 * destructive button to enable, and uses the mission-danger button variant.
 */
import React, { useState } from 'react';
import { useStore } from '../../store';
import { AlertTriangle, X, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import type { SiteRagReport } from '../../types/siteRag';
import { computeDeleteImpact, formatBytes, type DeleteMode } from '../../lib/reportDeleteCascade';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';

interface Props {
  report: SiteRagReport;
  open: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

function reportTitle(r: SiteRagReport): string {
  const typeDef = REPORT_TYPE_REGISTRY[(r.reportType ?? 'feasibility') as ReportTypeId];
  const typeLabel = typeDef?.shortLabel ?? 'Screening Assessment';
  return `${r.siteName} — ${typeLabel} v${r.version ?? 1}.${r.minorVersion ?? 0}`;
}

export const DeleteReportDialog: React.FC<Props> = ({ report, open, onClose, onDeleted }) => {
  const allReports = useStore.getState().siteRag.reports;
  const [mode, setMode] = useState<DeleteMode>('report-only');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const title = reportTitle(report);
  const impact = computeDeleteImpact(report, allReports, mode);
  const confirmMatches = confirmText.trim() === title;

  const handleDelete = async () => {
    if (!confirmMatches || busy) return;
    setBusy(true);
    try {
      const summary = await useStore.getState().deleteRagReport(report.id, mode);
      const parts: string[] = ['Report deleted'];
      if (summary.docsRemoved > 0) parts.push(`+ ${summary.docsRemoved} document${summary.docsRemoved === 1 ? '' : 's'}`);
      if (summary.sedRemoved) parts.push('+ SED');
      if (summary.bytesFreed > 0) parts[parts.length - 1] += ` (${formatBytes(summary.bytesFreed)} freed)`;
      toast.success(parts.join(' '));
      onClose();
      onDeleted?.();
    } catch (e) {
      toast.error(`Delete failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  };

  const Option: React.FC<{ value: DeleteMode; label: string; description: string }> = ({ value, label, description }) => (
    <label
      className={`flex items-start gap-2 p-2.5 border cursor-pointer transition-colors ${
        mode === value ? 'border-danger/40 bg-danger/[0.06]' : 'border-border-subtle hover:bg-state-hover'
      }`}
    >
      <input
        type="radio"
        name="delete-mode"
        value={value}
        checked={mode === value}
        onChange={() => setMode(value)}
        className="mt-0.5 accent-danger"
      />
      <div className="flex-1 min-w-0">
        <div className="type-section text-text-primary">{label}</div>
        <div className="text-[10px] text-text-muted mt-0.5">{description}</div>
      </div>
    </label>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-background w-[calc(100vw-2rem)] max-w-[560px] max-h-[90vh] overflow-y-auto shadow-2xl border border-border" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="w-9 h-9 border border-danger/40 bg-danger/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-danger" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">Delete report</h2>
            <p className="text-[10px] text-text-secondary mt-0.5">
              This cannot be undone. Pick what should be removed.
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-text-muted hover:text-text-primary"><X className="w-4 h-4" /></button>
        </div>

        {/* Report metadata */}
        <div className="px-5 py-3 border-b border-border-subtle bg-state-hover">
          <div className="flex items-start gap-2">
            <FileText className="w-3.5 h-3.5 text-text-secondary mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-[10px] font-bold text-text-primary">{title}</div>
              <div className="text-[10px] text-text-muted mt-0.5">
                {report.address} · created {new Date(report.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Mode selector */}
        <div className="p-5 space-y-2">
          <div className="type-section text-text-secondary mb-2">What to remove</div>
          <Option value="report-only" label="Report only" description="Removes the report file. Evidence (SED) and uploaded documents stay." />
          <Option value="with-evidence" label="Report + evidence (SED)" description="Also removes the Site Evidence Document — only if no other reports for this site reference it." />
          <Option value="with-everything" label="Report + evidence + uploaded documents" description="Also removes every uploaded document attached to this report." />
        </div>

        {/* Impact summary */}
        {(mode === 'with-evidence' || mode === 'with-everything') && (
          <div className="px-5 pb-3 space-y-2">
            {impact.sedBlockReason && (
              <div className="p-2 border border-warning/40 bg-warning/15 text-[10px] text-warning">
                ⚠ {impact.sedBlockReason}
              </div>
            )}
            {mode === 'with-everything' && impact.documentsToRemove.length > 0 && (
              <div className="p-2 border border-border bg-state-hover space-y-1">
                <div className="type-body font-bold uppercase tracking-widest text-text-muted">
                  {impact.documentsToRemove.length} document{impact.documentsToRemove.length === 1 ? '' : 's'} will be removed
                </div>
                <ul className="text-[10px] text-text-primary max-h-24 overflow-y-auto">
                  {impact.documentsToRemove.slice(0, 6).map(d => (
                    <li key={d.id} className="flex items-center justify-between gap-2 py-0.5">
                      <span className="truncate">{d.fileName}</span>
                      <span className="text-text-muted shrink-0">{formatBytes(d.sizeBytes || 0)}</span>
                    </li>
                  ))}
                  {impact.documentsToRemove.length > 6 && (
                    <li className="text-text-muted italic">… and {impact.documentsToRemove.length - 6} more</li>
                  )}
                </ul>
              </div>
            )}
            {impact.bytesFreed > 0 && (
              <div className="text-[10px] text-text-muted">
                Estimated storage freed: <span className="font-mono text-text-primary">{formatBytes(impact.bytesFreed)}</span>
              </div>
            )}
          </div>
        )}

        {/* Typed confirmation */}
        <div className="px-5 pb-3 space-y-1.5">
          <label className="type-section text-text-secondary">
            Type the report title to confirm
          </label>
          <div className="text-[10px] text-text-muted font-mono break-all bg-state-hover px-2 py-1 border border-border-subtle">
            {title}
          </div>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type the title above"
            className="w-full text-[11px] px-3 py-2 border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-danger font-mono"
            autoFocus
          />
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" className="h-8 type-body uppercase tracking-widest" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="mission-danger"
            size="sm"
            className="h-8 type-body uppercase tracking-widest"
            disabled={!confirmMatches || busy}
            onClick={handleDelete}
          >
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
};
